import React, { useState } from 'react';
import { API_BASE_URL } from '../../constants';
import { useTranslation } from 'react-i18next';

const SkillAddModal = ({ isOpen, onClose, onSave }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        Id_skill: '',
        upgrade_id: '',
        base_skill_id: '',
        Skill_name: '',
        Skill_name_jp: '',
        description: '',
        cost: '',
        rarity: '1',
        icon_id: '',
        color: '',
        Score_Value: ''
    });

    // Estados separados para Dependence (category + value)
    const [dependenceCategory, setDependenceCategory] = useState('');
    const [dependenceValue, setDependenceValue] = useState('');

    // Estados separados para Surface_Distance (category + value)
    const [surfaceCategory, setSurfaceCategory] = useState('');
    const [surfaceValue, setSurfaceValue] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Opciones de color
    const colorOptions = [
        { value: '', label: t('SkillEditModal.placeholders.select_color') },
        { value: 'Inherited Skills', label: 'Inherited Skills' },
        { value: 'Unique Skills', label: 'Unique Skills' },
        { value: 'Green Skills', label: 'Green Skills' },
        { value: 'Blue Skills', label: 'Blue Skills' },
        { value: 'Red Skills', label: 'Red Skills' },
        { value: 'Orange Skills', label: 'Orange Skills' },
        { value: 'Purple Skills', label: 'Purple Skills' },
        { value: 'Gold skills', label: 'Gold Skills' },
        { value: 'Scenario Gold Skill', label: 'Scenario Gold Skill' },
        { value: 'Evolution Skills', label: 'Evolution Skills' }
    ];

    // Opciones de categoría
    const categoryOptions = [
        { value: '', label: t('SkillEditModal.placeholders.select_type') },
        { value: 'Common', label: 'Common' },
        { value: 'Track', label: 'Track' },
        { value: 'Distance', label: 'Distance' },
        { value: 'Style', label: 'Style' }
    ];

    // Función para obtener valores según categoría
    const getValueOptions = (category) => {
        const options = {
            '': [{ value: '', label: t('SkillEditModal.placeholders.select_value') }],
            'Common': [{ value: 'Common', label: t('SkillEditModal.options.common') }],
            'Track': [
                { value: '', label: t('SkillEditModal.placeholders.select_generic') },
                { value: 'Turf', label: 'Turf' },
                { value: 'Dirt', label: 'Dirt' }
            ],
            'Distance': [
                { value: '', label: t('SkillEditModal.placeholders.select_generic') },
                { value: 'Sprint', label: 'Sprint' },
                { value: 'Mile', label: 'Mile' },
                { value: 'Medium', label: 'Medium' },
                { value: 'Long', label: 'Long' }
            ],
            'Style': [
                { value: '', label: t('SkillEditModal.placeholders.select_generic') },
                { value: 'Front', label: 'Front' },
                { value: 'Pace', label: 'Pace' },
                { value: 'Late', label: 'Late' },
                { value: 'End', label: 'End' }
            ]
        };
        return options[category] || options[''];
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handler para cambio de categoría de Dependence
    const handleDependenceCategoryChange = (e) => {
        const newCategory = e.target.value;
        setDependenceCategory(newCategory);

        if (newCategory === 'Common') {
            setDependenceValue('Common');
        } else {
            setDependenceValue('');
        }
    };

    // Handler para cambio de valor de Dependence
    const handleDependenceValueChange = (e) => {
        setDependenceValue(e.target.value);
    };

    // Handler para cambio de categoría de Surface_Distance
    const handleSurfaceCategoryChange = (e) => {
        const newCategory = e.target.value;
        setSurfaceCategory(newCategory);

        if (newCategory === 'Common') {
            setSurfaceValue('Common');
        } else {
            setSurfaceValue('');
        }
    };

    // Handler para cambio de valor de Surface_Distance
    const handleSurfaceValueChange = (e) => {
        setSurfaceValue(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Combinar los valores
            const finalData = {
                ...formData,
                Dependence: dependenceValue || '',
                Surface_Distance: surfaceValue || ''
            };

            const response = await fetch(`${API_BASE_URL}/skills`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(finalData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error creating skill');
            }

            const data = await response.json();

            if (onSave) {
                onSave(data.skill);
            }

            // Reset form
            setFormData({
                Id_skill: '',
                upgrade_id: '',
                base_skill_id: '',
                Skill_name: '',
                Skill_name_jp: '',
                description: '',
                cost: '',
                rarity: '1',
                icon_id: '',
                color: '',
                Score_Value: ''
            });
            setDependenceCategory('');
            setDependenceValue('');
            setSurfaceCategory('');
            setSurfaceValue('');

            onClose();
        } catch (err) {
            setError(err.message || 'Error creating skill');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-card-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-primary-green text-white px-6 py-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold">Add New Skill</h2>
                        <p className="text-sm text-green-100 mt-1">Create a new skill entry</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-green-600 rounded-lg p-2 transition-colors"
                        title={t('SkillEditModal.close')}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-red-800 text-sm font-medium">{error}</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* ── Separador de sección ── */}
                        <div className="md:col-span-2 flex items-center gap-3 pt-4 pb-1">
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                Identificación
                            </span>
                            <div className="flex-1 h-px bg-gray-100" />
                        </div>

                        {/* Id_skill */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                ID Skill (Game ID) *
                            </label>
                            <input
                                type="number"
                                name="Id_skill"
                                value={formData.Id_skill}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none"
                                placeholder="10011"
                            />
                        </div>

                        {/* icon_id */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Icon ID
                            </label>
                            <input
                                type="number"
                                name="icon_id"
                                value={formData.icon_id}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none"
                                placeholder="10011"
                            />
                        </div>

                        {/* upgrade_id */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Upgrade ID (Evolves to)
                            </label>
                            <input
                                type="number"
                                name="upgrade_id"
                                value={formData.upgrade_id}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none"
                                placeholder="Optional"
                            />
                        </div>

                        {/* base_skill_id */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Base Skill ID (Evolves from)
                            </label>
                            <input
                                type="number"
                                name="base_skill_id"
                                value={formData.base_skill_id}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none"
                                placeholder="Optional"
                            />
                        </div>

                        {/* ── Separador de sección ── */}
                        <div className="md:col-span-2 flex items-center gap-3 pt-4 pb-1">
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                Nombre
                            </span>
                            <div className="flex-1 h-px bg-gray-100" />
                        </div>

                        {/* Skill_name */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('SkillEditModal.labels.skill_name_en')}
                            </label>
                            <input
                                type="text"
                                name="Skill_name"
                                value={formData.Skill_name}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none"
                                placeholder="Right-Handed ○"
                            />
                        </div>

                        {/* Skill_name_jp */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('SkillEditModal.labels.skill_name_jp')}
                            </label>
                            <input
                                type="text"
                                name="Skill_name_jp"
                                value={formData.Skill_name_jp}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none"
                                placeholder="右回り○"
                            />
                        </div>

                        {/* ── Separador de sección ── */}
                        <div className="md:col-span-2 flex items-center gap-3 pt-4 pb-1">
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                Clasificación
                            </span>
                            <div className="flex-1 h-px bg-gray-100" />
                        </div>

                        {/* cost */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('SkillEditModal.labels.cost')}
                            </label>
                            <input
                                type="number"
                                name="cost"
                                value={formData.cost}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none"
                                placeholder="100"
                            />
                        </div>

                        {/* rarity */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('SkillEditModal.labels.rarity')}
                            </label>
                            <input
                                type="number"
                                name="rarity"
                                value={formData.rarity}
                                onChange={handleChange}
                                min="1"
                                max="5"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none"
                                placeholder="3"
                            />
                        </div>
                        {/* Preview visual de rarity — SOLO LECTURA */}
                        <div className="flex gap-0.5 mt-1" aria-hidden="true">
                            {[1, 2, 3, 4, 5].map(star => (
                                <span
                                    key={star}
                                    className={star <= parseInt(formData.rarity || 0) ? 'text-yellow-400 text-base' : 'text-gray-300 text-base'}
                                >
                                    ★
                                </span>
                            ))}
                        </div>

                        {/* color */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('SkillEditModal.labels.color_type')}
                            </label>
                            <select
                                name="color"
                                value={formData.color}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none"
                            >
                                {colorOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Score_Value */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('SkillEditModal.labels.score_value')}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                name="Score_Value"
                                value={formData.Score_Value}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none"
                                placeholder="120.50"
                            />
                        </div>

                        {/* Dependence - Cascading Dropdowns */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('SkillEditModal.labels.dependence')}
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                {/* Dropdown 1: Categoría */}
                                <div>
                                    <label className="block text-xs text-gray-600 mb-1">{t('SkillEditModal.labels.type')}</label>
                                    <select
                                        value={dependenceCategory}
                                        onChange={handleDependenceCategoryChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none text-sm"
                                    >
                                        {categoryOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Dropdown 2: Valor */}
                                <div>
                                    <label className="block text-xs text-gray-600 mb-1">{t('SkillEditModal.labels.value')}</label>
                                    <select
                                        value={dependenceValue}
                                        onChange={handleDependenceValueChange}
                                        disabled={!dependenceCategory || dependenceCategory === 'Common'}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    >
                                        {getValueOptions(dependenceCategory).map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1.5">
                                {dependenceCategory === 'Common' ? t('SkillEditModal.hints.dependence_common') : t('SkillEditModal.hints.dependence_specific')}
                            </p>
                        </div>

                        {/* Surface_Distance - Cascading Dropdowns */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('SkillEditModal.labels.surface')}
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                {/* Dropdown 1: Categoría */}
                                <div>
                                    <label className="block text-xs text-gray-600 mb-1">{t('SkillEditModal.labels.type')}</label>
                                    <select
                                        value={surfaceCategory}
                                        onChange={handleSurfaceCategoryChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none text-sm"
                                    >
                                        {categoryOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Dropdown 2: Valor */}
                                <div>
                                    <label className="block text-xs text-gray-600 mb-1">{t('SkillEditModal.labels.value')}</label>
                                    <select
                                        value={surfaceValue}
                                        onChange={handleSurfaceValueChange}
                                        disabled={!surfaceCategory || surfaceCategory === 'Common'}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    >
                                        {getValueOptions(surfaceCategory).map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1.5">
                                {surfaceCategory === 'Common' ? t('SkillEditModal.hints.surface_common') : t('SkillEditModal.hints.dependence_specific')}
                            </p>
                        </div>

                        {/* description */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('SkillEditModal.labels.description')}
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="4"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green outline-none resize-none"
                                placeholder={t('SkillEditModal.placeholders.description')}
                            />
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="bg-gray-50 -mx-6 -mb-6 mt-6 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200 sticky bottom-0">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50"
                        >
                            {t('SkillEditModal.actions.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-5 py-2.5 bg-primary-green text-white rounded-lg hover:bg-green-600 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Create Skill
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SkillAddModal;
